import { useGetAlbumVideoCommentsInfinite } from '@vimeo/gctl-api/hooks/useAlbumVideoComments';
import { useGetVideoCommentsInfinite } from '@vimeo/gctl-api/hooks/useVideoComments';
import { useGetVideoPrivateCommentsInfinite } from '@vimeo/gctl-api/hooks/useVideoPrivateComments';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import {
  getPublicCommentsCountQuery,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
} from '../../utils/helper';

type Option = {
  shouldFetch: boolean;
  videoVersionUri?: string;
  targetApiVersion?: string;
};

type FilterOptions = {
  videoVersionUri?: string;
  targetApiVersion?: string;
};

export function useGetCommentsCount(
  clipRequestId: string,
  isPublic: boolean,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const publicCommentsCount = useGetPublicCommentsCount(
    clipRequestId,
    { shouldFetch: isPublic },
    showcaseId,
  );
  const privateCommentsCount = useGetPrivateCommentsCount(
    clipRequestId,
    {
      shouldFetch: !isPublic,
      videoVersionUri: filterOptions?.videoVersionUri,
      targetApiVersion: filterOptions?.targetApiVersion,
    },
    reviewId,
  );

  return isPublic ? publicCommentsCount : privateCommentsCount;
}

export function useGetPrivateCommentsCount(
  clipRequestId: string,
  option: Option,
  reviewId?: string,
) {
  const password = getReviewPasswordHashFromCookie(reviewId);

  const { data, isLoading, error, mutate } = useGetVideoPrivateCommentsInfinite(
    () => {
      if (!clipRequestId || !option.shouldFetch) return null;

      return {
        where: { videoId: clipRequestId as unknown as number },
        select: ['uri'],
        query: {
          includeDeletedComments: true,
          page: 1,
          perPage: 1,
          versionUri: option?.videoVersionUri || undefined,
          reviewId,
          password,
        },
        headers: {
          ...(option?.targetApiVersion
            ? { Accept: `application/vnd.vimeo.*+json;version=${option.targetApiVersion}` }
            : {}),
        },
      };
    },
    {
      revalidateOnFocus: false,
      revalidateAll: true,
      revalidateFirstPage: false,
      revalidateIfStale: false,
      parallel: false,
    },
  );

  const increaseCommentsCount = () => {
    mutate((data) => {
      const filteredData = data
        ?.filter((page) => !!page)
        .map((set) => {
          return {
            ...set,
            data: [...set.data],
            total: set.total ? set.total + 1 : 1,
          };
        });
      return filteredData;
    });
  };

  const decreaseCommentsCount = () => {
    mutate((data) => {
      const filteredData = data
        ?.filter((page) => !!page)
        .map((set) => {
          return {
            ...set,
            data: [...set.data],
            total: set.total && set.total > 0 ? set.total - 1 : 0,
          };
        });
      return filteredData;
    });
  };

  return {
    // @ts-expect-error GCTL doesn't have a type for filteredTotal on the pager
    commentsCount: data?.[0]?.filteredTotal ?? 0,
    changeCommentsCount: { increaseCommentsCount, decreaseCommentsCount },
    revalidateCommentCount: mutate,
    isLoading,
    isReady: !!data || !!error,
    error,
  };
}

export function useGetPublicCommentsCount(
  clipRequestId: string,
  option: Option,
  showcaseId?: number,
) {
  const videoComments = useGetVideoCommentsInfinite(() => {
    if (!clipRequestId || !option.shouldFetch || showcaseId) return null;
    const password = getPasswordHashFromCookie(clipRequestId);
    return {
      where: { videoId: clipRequestId as unknown as number },
      select: ['uri'],
      query: getPublicCommentsCountQuery(password),
    };
  });

  const albumVideoComments = useGetAlbumVideoCommentsInfinite(() => {
    if (!clipRequestId || !option.shouldFetch || !showcaseId) return null;
    const password = getShowcasePasswordHashFromCookie(showcaseId);
    return {
      where: { albumId: showcaseId, videoId: clipRequestId as unknown as number },
      select: ['uri'],
      query: getPublicCommentsCountQuery(password),
    };
  });

  const { data, mutate, isLoading, error } = showcaseId ? albumVideoComments : videoComments;

  const increaseCommentsCount = () => {
    mutate((data) => {
      const filteredData = data
        ?.filter((page) => !!page)
        .map((set) => {
          return {
            ...set,
            data: [...set.data],
            // @ts-expect-error GCTL doesn't have a type for filteredTotal on the pager
            filteredTotal: set.filteredTotal ? set.filteredTotal + 1 : 1,
          };
        });
      return filteredData;
    });
  };

  const decreaseCommentsCount = () => {
    mutate((data) => {
      const filteredData = data
        ?.filter((page) => !!page)
        .map((set) => {
          return {
            ...set,
            data: [...set.data],
            // @ts-expect-error GCTL doesn't have a type for filteredTotal on the pager
            filteredTotal: set.filteredTotal && set.filteredTotal > 0 ? set.filteredTotal - 1 : 0,
          };
        });
      return filteredData;
    });
  };

  return {
    // @ts-expect-error GCTL doesn't have a type for filteredTotal on the pager
    commentsCount: data?.[0]?.filteredTotal ?? 0,
    changeCommentsCount: { increaseCommentsCount, decreaseCommentsCount },
    revalidateCommentCount: mutate,
    isLoading,
    isReady: !!data || !!error,
    error,
  };
}
